import React from "react";
import { Link } from "react-router-dom";
// We'll keep the import if you have data, otherwise the content is safe-guarded below
import { homeData } from "../data/homeData";

/* =========================
   1. DESIGN TOKENS (The "Executive" System)
   ========================= */
const TokenStyles = () => (
  <style>{`
    :root {
      /* Core Palette */
      --ink: #0F172A;
      --ink-70: #64748B;
      --ink-30: #CBD5E1;
      
      --bg-page: #F8FAFC;
      --bg-card: #FFFFFF;
      --line: #E2E8F0;
      
      /* Track Colors */
      --brand-blue: #2563EB;   /* Sales */
      --brand-purple: #7C3AED; /* Product */
      
      /* Shadows & Radius */
      --sh-xs: 0 1px 2px rgb(0 0 0 / .05);
      --sh-sm: 0 4px 6px -1px rgb(0 0 0 / .1);
      --sh-md: 0 10px 30px -12px rgb(0 0 0 / .15);
      
      --r-lg: 12px;
      --r-xl: 16px;
      
      /* Spacing */
      --space-4: 1rem;
      --space-8: 2rem;
      --space-12: 3rem;
      --space-24: 6rem;
    }

    /* Base Reset */
    * { box-sizing: border-box; }
    body { background: var(--bg-page); color: var(--ink); margin: 0; font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; -webkit-font-smoothing: antialiased; }
    
    /* Layout Shell */
    .shell { max-width: 80rem; margin: 0 auto; padding: 0 2rem; }
    @media (max-width: 768px) { .shell { padding: 0 1.5rem; } }

    /* Typography */
    h1, h2, h3 { font-family: Georgia, Cambria, "Times New Roman", Times, serif; letter-spacing: -0.01em; }
    h1 { font-size: 3.5rem; line-height: 1.1; font-weight: 700; color: var(--ink); margin-bottom: 1.5rem; }
    h2 { font-size: 1.75rem; font-weight: 600; margin-bottom: 0.75rem; }
    p { line-height: 1.6; color: var(--ink-70); }
    
    .label-caps { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }

    /* Cards */
    .card-grid { display: grid; grid-template-columns: 1fr; gap: 2rem; }
    @media (min-width: 768px) { .card-grid { grid-template-columns: 1fr 1fr; } }

    .feature-card {
      background: var(--bg-card);
      border: 1px solid var(--line);
      border-radius: var(--r-xl);
      padding: 2.5rem;
      position: relative;
      display: flex; flex-direction: column;
      height: 100%;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }
    .feature-card:hover { transform: translateY(-4px); box-shadow: var(--sh-md); }
    .feature-card:focus-within { ring: 2px solid var(--brand-blue); }

    /* Action Links */
    .action-link { display: inline-flex; align-items: center; gap: 0.5rem; font-weight: 600; margin-top: auto; font-size: 1rem; }
    
    /* Thought Leadership Strip */
    .sage-strip { 
      margin-top: var(--space-12); 
      padding-top: var(--space-8); 
      border-top: 1px solid var(--line); 
      text-align: center; 
    }
    .pill-cloud { display: flex; flex-wrap: wrap; justify-content: center; gap: 0.75rem; margin-top: 1.5rem; }
    .pill {
      background: #fff; border: 1px solid var(--line);
      padding: 0.4rem 1rem; border-radius: 99px;
      font-size: 0.85rem; color: var(--ink-70); font-weight: 500;
      transition: all 0.2s; cursor: default;
    }
    .pill:hover { border-color: var(--ink-30); color: var(--ink); transform: translateY(-1px); box-shadow: var(--sh-xs); }

    /* Footer */
    footer { margin-top: var(--space-24); padding: 2rem 0; border-top: 1px solid var(--line); text-align: center; font-size: 0.85rem; color: var(--ink-70); }
  `}</style>
);

/* =========================
   2. PAGE COMPONENT
   ========================= */
const HomePage = () => {
  return (
    <div
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <TokenStyles />

      {/* Main Content - No Nav, direct to Hero */}
      <main className="shell" style={{ flex: 1, paddingTop: "6rem" }}>
        {/* HERO SECTION */}
        <section style={{ textAlign: "center", marginBottom: "5rem" }}>
          <div style={{ display: "inline-block", marginBottom: "1.5rem" }}>
            <span
              className="label-caps"
              style={{
                background: "#F1F5F9",
                padding: "0.5rem 1rem",
                borderRadius: "99px",
                color: "var(--ink-70)",
              }}
            >
              The Enablement System
            </span>
          </div>
          <h1>
            Playbooks for <br />
            Revenue Excellence
          </h1>
          <p style={{ fontSize: "1.5rem", maxWidth: "42ch", margin: "0 auto" }}>
            Accelerating time-to-revenue and product mastery through role-based,
            measurable certification paths.
          </p>
        </section>

        {/* FEATURE CARDS */}
        <section className="card-grid">
          {/* TRACK 1: SALES */}
          <Link to="/sales-onboarding" style={{ textDecoration: "none" }}>
            <div
              className="feature-card"
              style={{ borderTop: "6px solid var(--brand-blue)" }}
            >
              <div
                className="label-caps"
                style={{ color: "var(--brand-blue)", marginBottom: "0.75rem" }}
              >
                Role-Based Ramp
              </div>
              <h2>Sales Onboarding</h2>
              <p style={{ fontSize: "1.1rem", marginBottom: "2rem", flex: 1 }}>
                A structured 4-phase framework moving sellers from hire to
                verified readiness (Certified Autonomy).
              </p>

              <ul
                style={{
                  paddingLeft: "0",
                  listStyle: "none",
                  marginBottom: "2rem",
                  color: "var(--ink)",
                }}
              >
                <li
                  style={{
                    marginBottom: "0.5rem",
                    display: "flex",
                    gap: "0.75rem",
                  }}
                >
                  <span style={{ color: "var(--brand-blue)" }}>•</span> 4-Phase
                  Ramp Structure
                </li>
                <li
                  style={{
                    marginBottom: "0.5rem",
                    display: "flex",
                    gap: "0.75rem",
                  }}
                >
                  <span style={{ color: "var(--brand-blue)" }}>•</span>{" "}
                  Competency Maps
                </li>
                <li
                  style={{
                    marginBottom: "0.5rem",
                    display: "flex",
                    gap: "0.75rem",
                  }}
                >
                  <span style={{ color: "var(--brand-blue)" }}>•</span>{" "}
                  Metric-driven Exits
                </li>
              </ul>

              <div
                className="action-link"
                style={{ color: "var(--brand-blue)" }}
              >
                Explore Framework <span>→</span>
              </div>
            </div>
          </Link>

          {/* TRACK 2: PRODUCT */}
          <Link to="/product-education" style={{ textDecoration: "none" }}>
            <div
              className="feature-card"
              style={{ borderTop: "6px solid var(--brand-purple)" }}
            >
              <div
                className="label-caps"
                style={{
                  color: "var(--brand-purple)",
                  marginBottom: "0.75rem",
                }}
              >
                Continuous Mastery
              </div>
              <h2>Product Education</h2>
              <p style={{ fontSize: "1.1rem", marginBottom: "2rem", flex: 1 }}>
                Moving from "knowledge transfer" to Performance Support, aligned
                to the 5 Moments of Need.
              </p>

              <ul
                style={{
                  paddingLeft: "0",
                  listStyle: "none",
                  marginBottom: "2rem",
                  color: "var(--ink)",
                }}
              >
                <li
                  style={{
                    marginBottom: "0.5rem",
                    display: "flex",
                    gap: "0.75rem",
                  }}
                >
                  <span style={{ color: "var(--brand-purple)" }}>•</span> The
                  Value Chain
                </li>
                <li
                  style={{
                    marginBottom: "0.5rem",
                    display: "flex",
                    gap: "0.75rem",
                  }}
                >
                  <span style={{ color: "var(--brand-purple)" }}>•</span>{" "}
                  Scenario Learning
                </li>
                <li
                  style={{
                    marginBottom: "0.5rem",
                    display: "flex",
                    gap: "0.75rem",
                  }}
                >
                  <span style={{ color: "var(--brand-purple)" }}>•</span> Impact
                  Measurement
                </li>
              </ul>

              <div
                className="action-link"
                style={{ color: "var(--brand-purple)" }}
              >
                Explore Framework <span>→</span>
              </div>
            </div>
          </Link>
        </section>

        {/* THOUGHT LEADERSHIP STRIP */}
        <section className="sage-strip">
          <h3>Guided by Industry Thought Leadership</h3>
          <p style={{ maxWidth: "60ch", margin: "0.5rem auto 2rem" }}>
            Our frameworks are built on proven methodologies, not opinions. We
            anchor execution in the world’s best thinking.
          </p>
          <div className="pill-cloud">
            {[
              "Mark Roberge",
              "The Challenger Sale",
              "MEDDICC",
              "Jill Konrath",
              "5 Moments of Need",
              "Bloom's Taxonomy",
              "Kirkpatrick Model",
              "The 6Ds",
            ].map((sage, i) => (
              <span key={i} className="pill">
                {sage}
              </span>
            ))}
          </div>
          <div style={{ marginTop: "3rem" }}>
            <Link
              to="/design-intent"
              style={{
                fontSize: "0.9rem",
                color: "var(--ink-70)",
                textDecoration: "underline",
                textUnderlineOffset: "4px",
              }}
            >
              View the Design Blueprint
            </Link>
          </div>
        </section>
      </main>

      <footer>
        <div className="shell">
          © {new Date().getFullYear()} Enablement Team • Internal Use Only
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
